import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Divider, Text } from "chameleon/lib/components";
import HelpTooltip from "chameleon/lib/components/help-tooltip/help-tooltip";

import CartItems from "checkout-experience/components/cart-items/cart-items";
import { displayMonetaryValue } from "checkout-experience/components/common/monetary-util";
import {
  firstColumnItem,
  grandTotalLine,
  lastColumnItem,
  paymentLinkCartItemInfo,
  summaryTable,
} from "checkout-experience/components/payment-link-summary/payment-link-summary.css";
import { SurchargeExtended } from "checkout-experience/hooks/use-surcharge";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";

const PaymentLinkSummary = ({
  surcharge,
}: {
  surcharge?: SurchargeExtended | null;
}) => {
  const { t, i18n } = useTranslation(["combined"]);
  const { checkoutSession } = useChexContext();
  const { orderSummary } = checkoutSession ?? {};

  const totalAmount = useMemo(() => {
    if (surcharge?.transactionFeeAmount && orderSummary?.totalAmount) {
      return {
        ...orderSummary?.totalAmount,
        amount:
          surcharge?.transactionFeeAmount?.amount +
          orderSummary?.totalAmount?.amount,
      };
    }
    return orderSummary?.totalAmount;
  }, [surcharge?.transactionFeeAmount, orderSummary?.totalAmount]);

  return orderSummary ? (
    <>
      <CartItems hideImages={true} />
      {surcharge?.purchaseTransactionFeePercent ? (
        <>
          <div className={paymentLinkCartItemInfo}>
            <div className={firstColumnItem}>
              <Text styleVariant="body" colorVariant="primaryText">
                {t("orderSummary.surcharge")} (
                {surcharge.purchaseTransactionFeePercent}%)
              </Text>
              <HelpTooltip
                content={t("orderSummary.surchargeTooltip") ?? ""}
                position="top"
                size="medium"
              />
            </div>
            <div className={lastColumnItem}>
              <Text styleVariant="body" colorVariant="primaryText">
                {displayMonetaryValue(
                  surcharge.transactionFeeAmount,
                  i18n.language
                )}
              </Text>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
      <Divider noMargins={true} />
      <table className={summaryTable}>
        <tbody>
          <tr className={grandTotalLine}>
            <th scope="row" className={firstColumnItem}>
              {t("orderSummary.grandTotal")}
            </th>
            <td className={lastColumnItem}>
              {displayMonetaryValue(totalAmount, i18n.language)}
            </td>
          </tr>
        </tbody>
      </table>
      <Text styleVariant="caption" colorVariant="secondaryText">
        {t("orderSummary.disclaimer")}
      </Text>
    </>
  ) : (
    <></>
  );
};

export default PaymentLinkSummary;
